const intEn = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 0,
});

/** US Pacific wall time (PST or PDT) for admin UI. */
export const ADMIN_TIME_ZONE = 'America/Los_Angeles';

const adminDateTimeFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: ADMIN_TIME_ZONE,
  dateStyle: 'short',
  timeStyle: 'medium',
});

const adminDateFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: ADMIN_TIME_ZONE,
  dateStyle: 'medium',
});

const adminTimeFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: ADMIN_TIME_ZONE,
  timeStyle: 'medium',
});

function toValidDate(v: string | number | Date): Date | null {
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Whole numbers with thousands separators (e.g. 12,345). */
export function formatInt(n: number): string {
  if (!Number.isFinite(n)) return '—';
  return intEn.format(Math.trunc(n));
}

/**
 * Numeric values that may include decimals (scores, etc.).
 * Uses grouping; trims trailing zeros when maxFractionDigits allows.
 */
export function formatNumber(
  n: number,
  options?: { maxFractionDigits?: number; minFractionDigits?: number }
): string {
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: options?.maxFractionDigits ?? 2,
    minimumFractionDigits: options?.minFractionDigits ?? 0,
  }).format(n);
}

/** Human-readable duration from stored seconds (e.g. session length). */
export function formatDurationSeconds(s: number | null | undefined): string {
  if (s == null || !Number.isFinite(s)) return '—';
  const secs = Math.round(s);
  if (secs < 60) return `${formatInt(secs)}s`;
  const m = Math.floor(secs / 60);
  const r = secs % 60;
  if (m < 60) return r > 0 ? `${m}m ${r}s` : `${m}m`;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return mm > 0 ? `${h}h ${mm}m` : `${h}h`;
}

/** mm:ss timer formatting for crossword solve times. */
export function formatMinutesSeconds(s: number | null | undefined): string {
  if (s == null || !Number.isFinite(s)) return '—';
  const secs = Math.max(0, Math.round(s));
  const minutes = Math.floor(secs / 60);
  const remaining = secs % 60;
  return `${String(minutes).padStart(2, '0')}:${String(remaining).padStart(2, '0')}`;
}

/** Absolute date+time in admin portal timezone (Pacific). */
export function formatAdminDateTime(v: string | number | Date | null | undefined): string {
  if (v == null) return '—';
  const d = toValidDate(v);
  return d ? adminDateTimeFormatter.format(d) : '—';
}

/** Calendar date only in admin portal timezone (Pacific). */
export function formatAdminDate(v: string | number | Date | null | undefined): string {
  if (v == null) return '—';
  const d = toValidDate(v);
  return d ? adminDateFormatter.format(d) : '—';
}

/** Clock time only in admin portal timezone (Pacific). */
export function formatAdminTime(v: string | number | Date | null | undefined): string {
  if (v == null) return '—';
  const d = toValidDate(v);
  return d ? adminTimeFormatter.format(d) : '—';
}

/** Strip legacy "Hmong word for …" prefix from stored crossword clues. */
export function formatCrosswordClue(clue: string): string {
  const stripped = clue.replace(/^Hmong word for\s+/i, '').trim();
  const unquoted = stripped.match(/^['"](.+)['"]$/);
  return unquoted ? unquoted[1] : stripped;
}

/** Matches clues produced by the generator fallback (e.g. "over there (2)"). */
const FALLBACK_VARIANT_CLUE_SUFFIX_RE = /\s+\(\d+\)$/;

/** True when the clue text is an auto-generated deduplication suffix. */
export function isFallbackVariantClue(clue: string): boolean {
  return FALLBACK_VARIANT_CLUE_SUFFIX_RE.test(clue.trim());
}

export function countFallbackVariantClues(clues: {
  across: Array<{ clue: string }>;
  down: Array<{ clue: string }>;
}): number {
  let count = 0;
  for (const row of [...clues.across, ...clues.down]) {
    if (isFallbackVariantClue(row.clue)) count += 1;
  }
  return count;
}
